import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { AgentProfileGuard } from 'app/core/auth/guards/agentProfile.guard';
import { MustChangePasswordGuard } from 'app/core/auth/guards/mustChangePassword.guard';
import { roleGuard } from 'app/core/auth/guards/role.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/home'
    {path: '', pathMatch : 'full', redirectTo: 'home'},

    // Redirect /sign-in to /auth/sign-in for convenience
    {path: 'sign-in', pathMatch: 'full', redirectTo: 'auth/sign-in'},

    // Redirect signed-in user to the '/admin/dashboard'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'admin/dashboard'},

    // ========================================
    // PUBLIC ROUTES (Sitio Web Público)
    // ========================================
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/public/home/home.routes')},
            {path: 'properties', loadChildren: () => import('app/modules/public/properties/properties.routes')},
            {path: 'property/:id', loadChildren: () => import('app/modules/public/property-detail/property-detail.routes')},
            {path: 'agents', loadChildren: () => import('app/modules/public/agents/agents.routes')},
            {path: 'contact', loadChildren: () => import('app/modules/public/contact/contact.routes')},
            // Shared listing - vista privada sin navegación para compartir con clientes
            {path: 'shared-listing', loadChildren: () => import('app/modules/public/shared-listing/shared-listing.routes')},
            // Offer form - formulario público para ofertas de compradores
            {path: 'offer', loadChildren: () => import('app/modules/public/offer-form/offer-form.routes')},
        ]
    },

    // ========================================
    // AUTH ROUTES (Autenticación de Agentes)
    // ========================================
    // Auth routes for guests
    {
        path: 'auth',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')}
        ]
    },

    // Auth routes accessible with OR without a session.
    // reset-password must be reachable even though the Supabase recovery link
    // establishes a session (NoAuthGuard would otherwise bounce the user to /admin).
    {
        path: 'auth',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: 'auth',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')},
            {path: 'complete-profile', loadChildren: () => import('app/modules/auth/complete-profile/complete-profile.routes')},
            {path: 'change-password', loadChildren: () => import('app/modules/auth/change-password/change-password.routes')}
        ]
    },

    // ========================================
    // ADMIN ROUTES (Panel Administrativo para Agentes)
    // ========================================
    {
        path: 'admin',
        canActivate: [AuthGuard, MustChangePasswordGuard, AgentProfileGuard],
        canActivateChild: [AuthGuard, MustChangePasswordGuard, AgentProfileGuard, roleGuard],
        component: LayoutComponent,
        data: {
            layout: 'enterprise'
        },
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'dashboard', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes')},
            {path: 'profile', loadChildren: () => import('app/modules/admin/profile/profile.routes')},
            {path: 'whatsapp', loadChildren: () => import('app/modules/admin/whatsapp/whatsapp.routes')},
            {path: 'properties', loadChildren: () => import('app/modules/admin/properties/properties.routes')},
            // ACM - Análisis Comparativo de Mercado
            {path: 'acm', loadChildren: () => import('app/modules/admin/acm/acm.routes')},
            // CRM
            {path: 'crm', loadChildren: () => import('app/modules/admin/crm/crm.routes')},
            // Clientes (contacts centralizados)
            {path: 'clients', loadChildren: () => import('app/modules/admin/clients/clients.routes')},
            // Legal
            {path: 'legal', loadChildren: () => import('app/modules/admin/legal/legal.routes')},
            // Cierres y Comisiones
            {path: 'closings', loadChildren: () => import('app/modules/admin/closings/closings.routes')},
            // Administración
            {path: 'management', loadChildren: () => import('app/modules/admin/management/management.routes')},
            // Usuarios
            {path: 'users', loadChildren: () => import('app/modules/admin/users/users.routes')},
            // Configuración
            {path: 'settings', loadChildren: () => import('app/modules/admin/settings/settings.routes')},
            // Mantener el ejemplo original por si acaso
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
        ]
    }
];
